/**
 * PRD-0004 SR-2 / S-6: the only upload error texts apps get. Older phone apps stored "Upload failed: <exception>",
 * which can quote a queued item (a notification's text); anything that isn't one of these becomes "Upload failed".
 */
const UPLOAD_ERRORS = /^(Upload failed( \((HTTP \d{3}|no connection to Omni|Omni took too long to answer)\))?|Signed out: open Omni and sign in again)$/;
export function safeUploadError(e) {
    if (typeof e !== "string" || !e)
        return null;
    return UPLOAD_ERRORS.test(e) ? e : "Upload failed";
}
/**
 * PRD-0004 S-3: `omni.device.capture.flush` starts an upload only for an app the phone records a source for,
 * with that source's `device:*` permission; for any other app it just returns the status.
 */
export function mayFlushCapture(appId, scopes, sources) {
    return sources.some((s) => s.enabled && s.app === appId && scopes.includes(`device:${s.source}`));
}
/** Items waiting longer than this since the last upload are worth telling the person about (FR-11). */
export const UPLOAD_STALE_MS = 30 * 60000;
/**
 * The upload status, also for older phone apps without `upload` (from `queued` / `lastError` /
 * `lastUpload`, AC-16). Null outside the phone app.
 */
export function captureUpload(st) {
    if (!st?.available)
        return null;
    if (st.upload)
        return { ...st.upload, lastError: safeUploadError(st.upload.lastError) };
    const queued = st.queued ?? 0;
    const error = safeUploadError(st.lastError); // S-6: an older phone app's raw text never reaches the screen
    const state = queued <= 0 ? "ok" : error && /^signed out/i.test(error) ? "signed_out" : error ? "failing" : "waiting";
    return { state, queued, lastUploadAt: st.lastUpload ?? null, lastAttemptAt: null, lastError: error, failingSince: null, dropped: 0, droppedAt: null };
}
/** "12,700": the same separators everywhere (FR-12). */
export function formatCount(n) {
    return Math.max(0, Math.round(n)).toLocaleString("en-US");
}
/**
 * FR-11: the upload problem to show instead of the "all fine" line, or null when there is none: the last
 * attempt failed, the phone is signed out, or items have waited more than 30 minutes since the last upload.
 * While a run is sending, "Uploading… N left" (UX §11, FR-13), so the count can be seen going down.
 * `noun` is "points" (Location) or "items" (Loggers).
 */
export function uploadNotice(u, noun, now = Date.now()) {
    if (!u || u.queued <= 0)
        return null;
    const n = formatCount(u.queued);
    // F-2: progress first, whenever a run is sending, however recent the last upload
    if (u.state === "uploading")
        return { state: u.state, text: `Uploading… ${n} left`, warn: false, canUpload: false };
    if (u.state === "ok")
        return null; // F-7: the phone says uploads work (items are fresh)
    const last = u.lastUploadAt ? Date.parse(u.lastUploadAt) : NaN;
    // Never uploaded: the phone app's "waiting" means items are older than its 15 minutes; an older phone app
    // (no lastAttemptAt) can't say, so a first point on a new install isn't a warning (F-7)
    const stale = Number.isNaN(last) ? u.state === "waiting" && !!u.lastAttemptAt : now - last > UPLOAD_STALE_MS;
    if (u.state !== "failing" && u.state !== "signed_out" && !stale)
        return null;
    const what = u.queued === 1 ? noun.slice(0, -1) : noun;
    switch (u.state) {
        case "signed_out":
            return { state: u.state, text: `${noun[0].toUpperCase()}${noun.slice(1)} waiting · Signed out: open Omni and sign in again`, warn: true, canUpload: false };
        case "offline":
            return { state: u.state, text: `${n} ${what} waiting · no connection`, warn: false, canUpload: false };
        case "failing":
            return { state: u.state, text: `${n} ${what} waiting · ${u.lastError || "Upload failed"}`, warn: true, canUpload: true };
        default:
            return { state: u.state, text: `${n} ${what} waiting to upload`, warn: true, canUpload: true };
    }
}
/** "28.09.2026": the date format of the PRD copy (UX §11), whatever the device's locale (F-5). Null for no date. */
export function formatDay(iso) {
    if (iso === null || iso === undefined || iso === "")
        return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return null;
    const p = (x) => String(x).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}
/** AC-19: "3 items couldn't be uploaded and were removed" (null when nothing was dropped). */
export function droppedNotice(u, noun) {
    if (!u || !u.dropped)
        return null;
    const what = u.dropped === 1 ? noun.slice(0, -1) : noun;
    return `${formatCount(u.dropped)} ${what} couldn't be uploaded and ${u.dropped === 1 ? "was" : "were"} removed`;
}
